// The eight connection-state cards from §s03 (execution plan §3/§7.3, WS-3
// step 4): Link open, Exchanging, Needs login, Pending expiration, Revoked,
// Error, Manual account, Remove-confirm. This file covers the six that
// describe an existing `ProviderItem` (needs-login through error) plus
// "manual"; "Link open"/"Exchanging"/"Remove-confirm" are transient dialog
// stages rendered by `ConnectStage`/`RemoveConnectionDialog` instead, per
// the plan's "deliberately NOT new kinds" note (§6.1).
//
// Reused inline on Accounts rows (`features/accounts` imports this file) --
// the one cross-feature import in the plan, and it is one-directional: this
// file never imports from `features/accounts`.
import { HonestInline } from '@/components/HonestInline';
import { ConnectionStatusPill } from '@/components/ConnectionStatusPill';
import { StatusDot } from '@/components/StatusDot';
import type { RouteStateKind } from '@/app/states/RouteStates';
import type { ProviderItem } from '@/api/hooks/useItems';

export interface ConnectionStateCardProps {
  item: ProviderItem;
  onReconnect?: (itemId: string) => void;
  onRemove?: (itemId: string) => void;
  reconnecting?: boolean;
}

const HONEST_KIND_BY_ITEM_STATUS: Partial<Record<ProviderItem['status'], RouteStateKind>> = {
  needs_login: 'item-needs-login',
  pending_expiration: 'item-pending-expiration',
  revoked: 'item-revoked',
  error: 'sync-failed',
};

/** A manual, non-Plaid connection has no ProviderItem at all -- it never reaches this card. */
export function ConnectionStateCard({ item, onReconnect, onRemove, reconnecting }: ConnectionStateCardProps) {
  const honestKind = HONEST_KIND_BY_ITEM_STATUS[item.status];
  const title = item.institution_name ?? 'Connected bank';

  if (item.status === 'active') {
    return (
      <div
        className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4"
        data-testid="connection-state-card"
        data-item-status={item.status}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="font-medium">{title}</span>
          {item.bootstrap_status === 'complete' && item.last_synced_at
            ? <ConnectionStatusPill status="synced" lastSyncedAt={item.last_synced_at} />
            : <span role="status">{item.bootstrap_status === 'failed' ? 'First sync needs attention' : 'First sync pending'}</span>}
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(item.item_id)}
            className="ms-tap-44 min-h-11 self-start text-xs text-money-negative underline"
          >
            Remove connection
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
      data-testid="connection-state-card"
      data-item-status={item.status}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium">{title}</span>
        {item.status === 'needs_login' || item.status === 'error' ? (
          <ConnectionStatusPill
            status={item.status}
            errorCode={item.error_code}
            consentExpiresAt={item.consent_expires_at ?? undefined}
          />
        ) : (
          <StatusDot
            tone={item.status === 'pending_expiration' ? 'warn' : 'neutral'}
            shape={item.status === 'pending_expiration' ? 'filled' : 'square'}
            label={item.status === 'pending_expiration' ? 'Access ends soon' : 'Revoked at the bank'}
            showLabel
          />
        )}
      </div>
      {honestKind && <HonestInline kind={honestKind} />}
      <div className="flex flex-wrap gap-2">
        {item.status !== 'revoked' && onReconnect && (
          <button
            type="button"
            onClick={() => onReconnect(item.item_id)}
            disabled={reconnecting}
            className="ms-tap-44 min-h-11 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
          >
            {reconnecting ? 'Reconnecting…' : 'Reconnect'}
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(item.item_id)}
            className="ms-tap-44 min-h-11 rounded-md border border-border px-3 py-1.5 text-xs"
          >
            Remove connection
          </button>
        )}
      </div>
    </div>
  );
}

export default ConnectionStateCard;
